import React, { useCallback, useMemo } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Dialog } from '../../common/Dialog';
import { DropZone } from './DropZone';
import { ImportFileList } from './ImportFileList';
import { ImportSettings } from './ImportSettings';
import { useAssetImport } from '../../../hooks/useAssetImport';
import { formatFileSize } from '../../../../core/types/import.types';
import { cn } from '../../../utils/cn';

export const AssetImportDialog: React.FC = () => {
  const {
    files,
    settings,
    isImporting,
    progress,
    isOpen,
    selectedFile,
    sortBy,
    sortOrder,
    stats,
    validation,
    canImport,
    closeDialog,
    addFiles,
    removeFile,
    clearFiles,
    selectFile,
    toggleFileSelected,
    selectAll,
    deselectAll,
    updateSettings,
    resetSettings,
    startImport,
    cancelImport,
    retryFailed,
    setSortBy,
    setSortOrder,
  } = useAssetImport();

  const handleSortOrderToggle = useCallback(() => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  }, [sortOrder, setSortOrder]);

  const handleClose = useCallback(() => {
    if (!isImporting) {
      closeDialog();
    }
  }, [isImporting, closeDialog]);

  // Import summary text
  const summaryText = useMemo(() => {
    if (stats.selected === 0) return 'No files selected';

    const parts: string[] = [`${stats.selected} file${stats.selected > 1 ? 's' : ''}`];
    parts.push(`(${formatFileSize(stats.totalSize)})`);

    return parts.join(' ');
  }, [stats]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnEscape={!isImporting}
      closeOnOverlayClick={!isImporting}
    >
      <div className="flex flex-col h-[80vh] max-h-[700px] bg-zinc-900">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700 bg-zinc-800/50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-600/20">
              <Upload size={20} className="text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">Import Assets</h2>
              <p className="text-sm text-zinc-400">
                Add files to your project
              </p>
            </div>
          </div>
          {!isImporting && (
            <button
              onClick={handleClose}
              className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-zinc-200 transition-colors"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-1 min-h-0">
          {/* Main area */}
          <div className="flex-1 flex flex-col min-w-0">
            {files.length === 0 ? (
              /* Drop zone when no files */
              <div className="flex-1 p-6">
                <DropZone onFilesAdded={addFiles} disabled={isImporting} />
              </div>
            ) : (
              /* File list with mini drop zone */
              <>
                {!isImporting && (
                  <div className="p-3 border-b border-zinc-700/50">
                    <DropZone onFilesAdded={addFiles} compact />
                  </div>
                )}
                <ImportFileList
                  files={files}
                  selectedFileId={selectedFile?.id || null}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSelectFile={selectFile}
                  onToggleChecked={toggleFileSelected}
                  onRemoveFile={removeFile}
                  onConfigureFile={(id) => selectFile(id)}
                  onSelectAll={selectAll}
                  onDeselectAll={deselectAll}
                  onClearAll={clearFiles}
                  onSortChange={setSortBy}
                  onSortOrderToggle={handleSortOrderToggle}
                />
              </>
            )}
          </div>

          {/* Settings sidebar */}
          {files.length > 0 && (
            <div className="w-72 flex-shrink-0">
              <ImportSettings
                settings={settings}
                onUpdateSettings={updateSettings}
                onResetSettings={resetSettings}
              />
            </div>
          )}
        </div>

        {/* Progress bar (full width during import) */}
        {isImporting && (
          <div className="h-1 bg-zinc-800">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress.currentProgress}%` }}
            />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-700 bg-zinc-800/50">
          {/* Left side - status/progress */}
          <div className="flex items-center gap-3">
            {isImporting ? (
              <>
                <Loader2 size={18} className="text-blue-400 animate-spin" />
                <div>
                  <p className="text-sm text-zinc-200">
                    Importing... {progress.completed}/{progress.total}
                  </p>
                  {progress.currentFile && (
                    <p className="text-xs text-zinc-400 truncate max-w-[250px]">
                      {progress.currentFile}
                    </p>
                  )}
                </div>
              </>
            ) : stats.failed > 0 ? (
              <>
                <AlertCircle size={18} className="text-red-400" />
                <span className="text-sm text-red-400">
                  {stats.failed} file{stats.failed > 1 ? 's' : ''} failed to import
                </span>
              </>
            ) : stats.completed > 0 ? (
              <>
                <CheckCircle size={18} className="text-green-400" />
                <span className="text-sm text-green-400">
                  {stats.completed} file{stats.completed > 1 ? 's' : ''} imported successfully
                </span>
              </>
            ) : (
              <span className="text-sm text-zinc-400">{summaryText}</span>
            )}
          </div>

          {/* Right side - actions */}
          <div className="flex items-center gap-3">
            {!validation.isValid && validation.errors.length > 0 && files.length > 0 && (
              <span className="text-xs text-amber-400 max-w-[200px] truncate">
                {validation.errors[0]}
              </span>
            )}

            {isImporting ? (
              <button
                onClick={cancelImport}
                className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
            ) : (
              <>
                {stats.failed > 0 && (
                  <button
                    onClick={retryFailed}
                    className="px-4 py-2 text-sm font-medium text-amber-300 hover:text-amber-200 bg-amber-600/20 hover:bg-amber-600/30 rounded-lg transition-colors"
                  >
                    Retry Failed
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
                >
                  {stats.completed > 0 ? 'Done' : 'Cancel'}
                </button>
                <button
                  onClick={startImport}
                  disabled={!canImport}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    canImport
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  )}
                >
                  <Upload size={16} />
                  Import {stats.selected > 0 && `(${stats.selected})`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
